// @angular
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  ChangeDetectionStrategy
} from "@angular/core";

// Translate
import { TranslateModule } from "@ngx-translate/core";
// Components and modules
import { MatButtonModule } from "@angular/material/button";
// Zxing
import { ZXingScannerComponent, ZXingScannerModule } from "@zxing/ngx-scanner";
import { BrowserCodeReader, IScannerControls } from "@zxing/browser";

BrowserCodeReader.prototype.decodeFromVideoDevice = async function (
  deviceId: string | undefined,
  previewElem: string | HTMLVideoElement | undefined,
  callbackFn: any
): Promise<IScannerControls> {
  let videoConstraints = {
    deviceId: { exact: deviceId },
    width: { min: 640, ideal: 1920 },
    height: { min: 400, ideal: 1080 },
    aspectRatio: { ideal: 1.7777777778 },
    advanced: [{ zoom: 1 }]
  } as MediaTrackConstraintSet;
  const constraints: MediaStreamConstraints = { video: videoConstraints };
  return this.decodeFromConstraints(constraints, previewElem, callbackFn);
};

/**
 * Camera barcode/QR scanner (ZXing) that prefers the rear camera and emits the decoded text through `scannerResult`.
 * `width`/`height` size the viewport, `scannerZoomIn` toggles camera zoom; `hideZoom`, `hideSearching` and `hideScannerResult` hide parts of the UI.
 */
@Component({
  selector: "app-code-scanner",
  templateUrl: "./code-scanner.component.html",
  styleUrls: ["./code-scanner.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [MatButtonModule, TranslateModule, ZXingScannerModule]
})
export class CodeScannerComponent implements AfterViewInit {
  /***************************************************************************/
  // ANCHOR Variables
  /***************************************************************************/

  @Input() height: number;
  @Input() width: number;
  @Input() hideZoom: boolean;
  @Input() hideSearching: boolean;
  @Input() hideScannerResult: boolean;
  scannerFormats: string[] = ["QR_CODE", "EAN_13", "CODE_39", "CODE_128", "DATA_MATRIX"];
  scannerError: string;
  scannerEnabled: boolean = false;
  @Input()
  get scannerZoomIn(): boolean {
    return this._scannerZoomIn;
  }
  set scannerZoomIn(scannerZoomIn: boolean) {
    this._scannerZoomIn = scannerZoomIn;
    if (this.scannerEnabled) {
      this.setCameraZoom();
    }
  }
  _scannerZoomIn: boolean;
  @ViewChild("scanner", { static: false }) scanner: ZXingScannerComponent;
  scannerCamera: any;
  @Output() scannerResult = new EventEmitter<string>();
  @ViewChild("scannerContainer") scannerContainer: ElementRef;

  /***************************************************************************/
  // ANCHOR Constructor
  /***************************************************************************/

  constructor() {}

  /***************************************************************************/
  // ANCHOR Run after render
  /***************************************************************************/

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.scannerContainer.nativeElement.style.setProperty(
        "--webcam-container-width",
        this.width + "px"
      );
      this.scannerContainer.nativeElement.style.setProperty(
        "--webcam-container-height",
        this.height + "px"
      );
    }, 0);
  }

  /***************************************************************************/
  // ANCHOR Methods
  /***************************************************************************/

  scanHandler(code: string) {
    this.scannerResult.emit(code);
  }

  scanErrorHandler(error: string): void {
    this.scannerError = error;
  }

  setCameraZoom(): void {
    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: "environment" } })
      .then(mediaStream => {
        const [track] = mediaStream.getVideoTracks();
        track.applyConstraints({
          advanced: [{ zoom: this._scannerZoomIn ? 3 : 1 }]
        } as MediaTrackConstraintSet);
        // .then(() => {
        // })
        // .catch((e) => {
        // });
      });
  }

  // Select the rear camera
  camerasFoundHandler(cameras): void {
    setTimeout(() => {
      const backCamera = cameras.find(f => /back|rear|environment/gi.test(f.label)); // Default Back Facing Camera
      if (this.scannerCamera?.deviceId !== backCamera?.deviceId) {
        this.scannerCamera = backCamera;
      }
      setTimeout(() => (this.scannerEnabled = true), 0);
    }, 0);
  }
}
